import {
  twinsGetSuggestedCategories,
  twinsListInsightCategories,
  twinsListInsights,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useActiveOrganization } from './use-organization'
import { useCallback, useLayoutEffect, useState } from 'react'
import { TwinInsightCategory, TwinInsightsWithLatest } from 'api/model'
import { insightCategoriesAtom, insightsCategoriesLoadingAtom } from 'state/insights'
import { useRecoilState } from 'recoil'

type TwinsListInsightsOptions = {
  include_latest?: boolean
  source?: string
}

function useFetchTwinsListInsights(twinId: string, options?: TwinsListInsightsOptions) {
  const [activeOrganization] = useActiveOrganization()
  const [twinInsightsWithLatest, setTwinInsightsWithLatest] = useState<TwinInsightsWithLatest | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchTwinInsightsWithLatest = useCallback(async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await twinsListInsights(activeOrganization, twinId, {
        include_latest: options?.include_latest,
        source: options?.source,
      })
      setTwinInsightsWithLatest(response.data)
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId, options?.include_latest, options?.source])

  useLayoutEffect(() => {
    refetchTwinInsightsWithLatest()
  }, [refetchTwinInsightsWithLatest])

  return {
    isLoading,
    twinInsightsWithLatest,
    error,
    setTwinInsightsWithLatest,
    refetchTwinInsightsWithLatest,
  }
}

function useFetchInsightCategories(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [insightCategories, setInsightCategories] = useRecoilState<TwinInsightCategory[]>(insightCategoriesAtom)

  const [isLoading, setIsLoading] = useRecoilState<boolean>(insightsCategoriesLoadingAtom)
  const [error, setError] = useState<string | null>(null)

  const refetchInsightCategories = useCallback(async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await twinsListInsightCategories(activeOrganization, twinId)
      if (response.data) {
        setInsightCategories(response.data)
      }
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId, setInsightCategories, setIsLoading])

  useLayoutEffect(() => {
    refetchInsightCategories()
  }, [refetchInsightCategories])

  return {
    isLoading,
    insightCategories,
    error,
    setInsightCategories,
    refetchInsightCategories,
  }
}

function useFetchSuggestedCategories(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [suggestedCategories, setSuggestedCategories] = useState<string[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchSuggestedCategories = useCallback(async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await twinsGetSuggestedCategories(activeOrganization, twinId)
      if (response.data) {
        setSuggestedCategories(response.data)
      }
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId])

  useLayoutEffect(() => {
    refetchSuggestedCategories()
  }, [refetchSuggestedCategories])

  return {
    isLoading,
    suggestedCategories,
    error,
    setSuggestedCategories,
    refetchSuggestedCategories,
  }
}

export { useFetchTwinsListInsights, useFetchInsightCategories, useFetchSuggestedCategories }
